'use client'

/**
 * Config-driven admin sidebar: primary items + optional Advanced link.
 * When config.admin.primaryItems is set, shows only those + Advanced.
 * When omitted, shows full admin menu.
 */
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import type { ShellConfig } from '../../config/shell-config'
import { getShellAdminMenuItems } from '../../pages/admin/menu-items'

const ADVANCED_HREF = '/admin/settings'
const ADVANCED_LABEL_DEFAULT = 'Advanced Settings'
const ADVANCED_ICON = 'ri-settings-3-line'

type AdminSidebarProps = {
  config: ShellConfig
}

function NavItem({
  href,
  label,
  description,
  icon,
  isActive,
}: {
  href: string
  label: string
  description?: string
  icon?: string
  isActive: boolean
}) {
  return (
    <Link
      href={href}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
        isActive ? 'font-semibold' : 'hover:bg-background-default'
      }`}
      style={{
        backgroundColor: isActive ? 'var(--background-default)' : 'transparent',
        color: isActive ? 'var(--primary-default)' : 'var(--foreground-default)',
      }}
    >
      {icon != null && <i className={`${icon} text-lg flex-shrink-0`} aria-hidden />}
      <div className="flex-1 min-w-0">
        <div>{label}</div>
        {description != null && (
          <div className="text-xs text-foreground-muted truncate">{description}</div>
        )}
      </div>
    </Link>
  )
}

export function AdminSidebar({ config }: AdminSidebarProps) {
  const pathname = ((v) => v !== null && v !== undefined ? v : '')(usePathname())
  const adminConfig = config.admin
  const primaryItems = adminConfig?.primaryItems
  const advancedLabel = adminConfig?.advancedLabel ?? ADVANCED_LABEL_DEFAULT
  const showAdvanced = primaryItems != null && advancedLabel !== false

  const items =
    primaryItems != null && primaryItems.length > 0
      ? primaryItems
      : getShellAdminMenuItems()

  const isActive = (href: string) =>
    pathname === href || (href !== '/admin' && pathname.startsWith(href))

  return (
    <aside className="w-full lg:w-64 flex-shrink-0">
      <div className="rounded-lg border korvo-border-border korvo-bg-background-secondary p-4 space-y-2 sticky top-4 shadow-subtle">
        <div
          className="mb-4 pb-4 border-b"
          style={{ borderColor: 'var(--border-default)' }}
        >
          <h2 className="text-lg font-semibold text-foreground-default">Admin Panel</h2>
          {adminConfig?.subtitle != null && (
            <p className="text-xs text-foreground-muted mt-1">{adminConfig.subtitle}</p>
          )}
        </div>
        <nav className="space-y-1">
          {items.map((item) => (
            <NavItem
              key={item.id}
              href={item.href}
              label={item.label}
              description={item.description}
              icon={item.icon}
              isActive={isActive(item.href)}
            />
          ))}
          {showAdvanced && (
            <NavItem
              href={ADVANCED_HREF}
              label={typeof advancedLabel === 'string' ? advancedLabel : ADVANCED_LABEL_DEFAULT}
              description="System configuration"
              icon={ADVANCED_ICON}
              isActive={isActive(ADVANCED_HREF)}
            />
          )}
        </nav>
      </div>
    </aside>
  )
}
